import React, { useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '../App';
import { Navigation } from '../components/Navigation';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { 
  Shield, Users, Video, Calendar, BarChart3, DollarSign, 
  TrendingUp, UserCheck, Trash2, Crown, Check, X, ArrowLeft, PlusCircle, MapPin, Clock, Play, Upload, Link, Eye, EyeOff, UserPlus, TrendingDown, Flag, Mail, Trophy, Tag, Send, Pencil
} from 'lucide-react';
import { toast } from 'sonner';
import EditMemberModal from '../components/EditMemberModal';

export default function Admin() {
  const { user, loading, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [signupActivity, setSignupActivity] = useState(null);
  const [members, setMembers] = useState([]);
  const [stories, setStories] = useState([]);
  const [events, setEvents] = useState([]);
  const [pendingEvents, setPendingEvents] = useState([]);
  const [promoVideos, setPromoVideos] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [activeTab, setActiveTab] = useState('analytics');
  const [sendingDigest, setSendingDigest] = useState(false);
  const [resendingWelcome, setResendingWelcome] = useState({}); // { [userId]: true }
  const [editingMember, setEditingMember] = useState(null); // set to a member object → opens EditMemberModal
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const videoFileRef = useRef(null);
  const [newPromo, setNewPromo] = useState({
    title: '',
    description: '',
    video_url: '',
    embed_url: '',
    external_link: '',
    is_active: true
  });
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    location: '',
    event_type: 'weekly_game',
    max_participants: '',
    entry_fee: ''
  });

  useEffect(() => {
    if (!loading && !user) {
      navigate('/');
      return;
    }
    if (!loading && user && !user.is_admin) {
      toast.error('Director access required');
      navigate('/dashboard');
      return;
    }
  }, [loading, user, navigate]);

  useEffect(() => {
    if (user?.is_admin) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    setLoadingData(true);
    try {
      const [analyticsRes, signupRes, membersRes, storiesRes, eventsRes, pendingRes, promoRes] = await Promise.all([
        fetch(`${API}/admin/analytics`, { credentials: 'include' }),
        fetch(`${API}/admin/signup-activity`, { credentials: 'include' }),
        fetch(`${API}/admin/members`, { credentials: 'include' }),
        fetch(`${API}/admin/stories?limit=50`, { credentials: 'include' }),
        fetch(`${API}/events?upcoming=false`, { credentials: 'include' }),
        fetch(`${API}/admin/events/pending`, { credentials: 'include' }),
        fetch(`${API}/promo-videos?active_only=false`, { credentials: 'include' })
      ]);

      if (analyticsRes.ok) setAnalytics(await analyticsRes.json());
      if (signupRes.ok) setSignupActivity(await signupRes.json());
      if (membersRes.ok) setMembers(await membersRes.json());
      if (storiesRes.ok) setStories(await storiesRes.json());
      if (eventsRes.ok) setEvents(await eventsRes.json());
      if (pendingRes.ok) setPendingEvents(await pendingRes.json());
      if (promoRes.ok) setPromoVideos(await promoRes.json());
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoadingData(false);
    }
  };

  const toggleMembership = async (userId, currentStatus) => {
    try {
      const response = await fetch(`${API}/admin/members/${userId}/membership?is_member=${!currentStatus}`, {
        method: 'PUT',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success(`Membership ${!currentStatus ? 'granted' : 'revoked'}`);
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to update membership');
    }
  };

  const toggleAdmin = async (userId, currentStatus) => {
    try {
      const response = await fetch(`${API}/admin/members/${userId}/role?is_admin=${!currentStatus}`, {
        method: 'PUT',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success(`Director role ${!currentStatus ? 'granted' : 'revoked'}`);
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const setMemberRole = async (userId, role) => {
    try {
      const response = await fetch(`${API}/admin/members/${userId}/set-role?role=${role}`, {
        method: 'PUT',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success(`Role set to ${role}`);
        fetchData();
      } else {
        toast.error('Failed to update role');
      }
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const deleteMember = async (userId) => {
    try {
      const response = await fetch(`${API}/admin/members/${userId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Member deleted');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to delete member');
    }
  };

  const deleteStory = async (storyId) => {
    try {
      const response = await fetch(`${API}/admin/stories/${storyId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Story deleted');
        setStories(stories.filter(s => s.story_id !== storyId));
      }
    } catch (error) {
      toast.error('Failed to delete story');
    }
  };

  const deleteEvent = async (eventId) => {
    try {
      const response = await fetch(`${API}/admin/events/${eventId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Event deleted');
        setEvents(events.filter(e => e.event_id !== eventId));
        setPendingEvents(pendingEvents.filter(e => e.event_id !== eventId));
      }
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const approveEvent = async (eventId) => {
    try {
      const response = await fetch(`${API}/admin/events/${eventId}/approve`, {
        method: 'PUT',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Event approved and published!');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to approve event');
      }
    } catch (error) {
      toast.error('Failed to approve event');
    }
  };

  const rejectEvent = async (eventId) => {
    try {
      const response = await fetch(`${API}/admin/events/${eventId}/reject`, {
        method: 'PUT',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Event rejected');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reject event');
      }
    } catch (error) {
      toast.error('Failed to reject event');
    }
  };

  // Admin: manually trigger the Sunday weekend digest email now
  const sendWeekendDigest = async () => {
    if (sendingDigest) return;
    if (!window.confirm('Send the weekend event digest to all opted-in members right now?')) return;
    setSendingDigest(true);
    try {
      const response = await fetch(`${API}/admin/notifications/weekend-digest`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok) {
        const detail = data?.detail || {};
        if (detail.skipped) {
          toast('No upcoming events in the next 30 days — nothing to send.');
        } else {
          toast.success(`Digest sent to ${detail.sent || 0} members · ${detail.total_events || 0} events`);
        }
      } else {
        toast.error(data?.detail || 'Failed to send digest');
      }
    } catch (err) {
      console.error(err);
      toast.error('Network error sending digest');
    } finally {
      setSendingDigest(false);
    }
  };

  // Admin: one-shot backfill for birdie stats on legacy leaderboard entries
  const [backfillingBirdies, setBackfillingBirdies] = useState(false);
  const backfillBirdieStats = async () => {
    if (backfillingBirdies) return;
    if (!window.confirm('Recompute birdie stats for all posted rounds?\n\nThis re-scans every leaderboard entry and updates it if the parent post has full hole-by-hole scorecard data. Safe to run multiple times.')) return;
    setBackfillingBirdies(true);
    try {
      const response = await fetch(`${API}/admin/backfill-birdies`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok && data.success) {
        toast.success(`Backfill complete · scanned ${data.scanned} · updated ${data.updated} · skipped ${data.skipped_no_post + data.skipped_no_scorecard + data.skipped_no_match}`);
      } else {
        toast.error(data?.detail || 'Backfill failed');
      }
    } catch (err) {
      console.error(err);
      toast.error('Network error running backfill');
    } finally {
      setBackfillingBirdies(false);
    }
  };


  // Admin: regenerate a temp password and re-fire the welcome email for a specific user
  const resendWelcomeEmail = async (member) => {
    const memberEmail = member?.email;
    const memberId = member?.user_id;
    if (!memberId || !memberEmail) return;
    if (resendingWelcome[memberId]) return;
    if (!window.confirm(`Send a fresh welcome email to ${member.name || memberEmail} with a new temporary password?\n\nTheir current password will stop working.`)) return;
    setResendingWelcome(prev => ({ ...prev, [memberId]: true }));
    try {
      const res = await fetch(`${API}/admin/users/${memberId}/resend-welcome`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || `Welcome email re-sent to ${memberEmail}`);
      } else {
        toast.error(data?.detail || 'Failed to re-send welcome email');
      }
    } catch (err) {
      console.error(err);
      toast.error('Network error re-sending welcome email');
    } finally {
      setResendingWelcome(prev => {
        const next = { ...prev };
        delete next[memberId];
        return next;
      });
    }
  };

  // Promo Video Functions
  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['video/mp4', 'video/quicktime', 'video/webm'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a valid video file (MP4, MOV, WebM)');
      return;
    }

    if (file.size > 500 * 1024 * 1024) {
      toast.error(`Video too large (${(file.size / 1024 / 1024).toFixed(0)}MB). Max size is 500MB — try trimming the clip.`);
      return;
    }

    setUploadingVideo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API}/upload/media`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setNewPromo(prev => ({ ...prev, video_url: data.media_url, embed_url: '' }));
        toast.success('Video uploaded!');
      } else {
        toast.error('Failed to upload video');
      }
    } catch (error) {
      toast.error('Upload error');
    } finally {
      setUploadingVideo(false);
    }
  };

  const savePromoVideo = async () => {
    if (!newPromo.title) {
      toast.error('Please enter a title');
      return;
    }

    if (!newPromo.video_url && !newPromo.embed_url) {
      toast.error('Please upload a video or enter a YouTube/Vimeo URL');
      return;
    }

    try {
      const url = editingPromo 
        ? `${API}/promo-videos/${editingPromo.video_id}`
        : `${API}/promo-videos`;
      
      const response = await fetch(url, {
        method: editingPromo ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newPromo)
      });

      if (response.ok) {
        toast.success(editingPromo ? 'Video updated!' : 'Video added!');
        setShowPromoModal(false);
        setEditingPromo(null);
        setNewPromo({ title: '', description: '', video_url: '', embed_url: '', external_link: '', is_active: true });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save video');
      }
    } catch (error) {
      toast.error('Error saving video');
    }
  };

  const deletePromoVideo = async (videoId) => {
    try {
      const response = await fetch(`${API}/promo-videos/${videoId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Video deleted');
        setPromoVideos(promoVideos.filter(v => v.video_id !== videoId));
      }
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  const togglePromoVideo = async (videoId) => {
    try {
      const response = await fetch(`${API}/promo-videos/${videoId}/toggle`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to toggle video');
    }
  };

  const openEditPromo = (video) => {
    setEditingPromo(video);
    setNewPromo({
      title: video.title,
      description: video.description || '',
      video_url: video.video_url || '',
      embed_url: video.embed_url || '',
      external_link: video.external_link || '',
      is_active: video.is_active
    });
    setShowPromoModal(true);
  };

  const createEvent = async (e) => {
    e.preventDefault();
    try {
      const eventData = {
        ...newEvent,
        max_participants: newEvent.max_participants ? parseInt(newEvent.max_participants) : null,
        entry_fee: newEvent.entry_fee ? parseFloat(newEvent.entry_fee) : null
      };
      
      const response = await fetch(`${API}/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(eventData)
      });
      
      if (response.ok) {
        toast.success('Event created successfully!');
        setShowCreateEvent(false);
        setNewEvent({
          title: '',
          description: '',
          date: '',
          time: '',
          location: '',
          event_type: 'weekly_game',
          max_participants: '',
          entry_fee: ''
        });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create event');
      }
    } catch (error) {
      toast.error('Failed to create event');
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bundle-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-bundle-green"></div>
      </div>
    );
  }

  if (!user.is_admin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-bundle-cream pb-mobile-nav md:pb-8">
      <Navigation user={user} onLogout={logout} />

      <div className="max-w-7xl mx-auto px-4 md:px-12 pt-20 md:pt-24">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-4 text-bundle-green hover:bg-bundle-cream-dark -ml-2"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Dashboard
        </Button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-bundle-gold rounded-xl flex items-center justify-center">
            <Shield className="w-6 h-6 text-bundle-green-dark" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-3xl text-bundle-green">Golf Club Director Panel</h1>
            <p className="font-body text-gray-600">Manage members, content, and view analytics</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-bundle-cream-dark mb-6">
            <TabsTrigger value="analytics" className="data-[state=active]:bg-bundle-green data-[state=active]:text-white">
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="members" className="data-[state=active]:bg-bundle-green data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" />
              Members
            </TabsTrigger>
            <TabsTrigger value="stories" className="data-[state=active]:bg-bundle-green data-[state=active]:text-white">
              <Video className="w-4 h-4 mr-2" />
              Stories
            </TabsTrigger>
            <TabsTrigger value="events" className="data-[state=active]:bg-bundle-green data-[state=active]:text-white">
              <Calendar className="w-4 h-4 mr-2" />
              Events
            </TabsTrigger>
            <TabsTrigger value="promos" className="data-[state=active]:bg-bundle-green data-[state=active]:text-white">
              <Play className="w-4 h-4 mr-2" />
              Promo Videos
            </TabsTrigger>
            <TabsTrigger value="invites" className="data-[state=active]:bg-bundle-green data-[state=active]:text-white" data-testid="admin-tab-invites">
              <Mail className="w-4 h-4 mr-2" />
              Invites
            </TabsTrigger>
            <TabsTrigger value="tags" className="data-[state=active]:bg-bundle-green data-[state=active]:text-white" data-testid="admin-tab-tags">
              <Tag className="w-4 h-4 mr-2" />
              Tags
            </TabsTrigger>
            <TabsTrigger value="quick-create" className="data-[state=active]:bg-bundle-green data-[state=active]:text-white" data-testid="admin-tab-quick-create">
              <UserPlus className="w-4 h-4 mr-2" />
              Add User
            </TabsTrigger>
          </TabsList>

          {/* Quick Actions */}
          <div className="mb-6 flex gap-3 flex-wrap">
            <Button 
              variant="outline" 
              onClick={() => navigate('/admin/rounds')}
              className="border-bundle-green text-bundle-green hover:bg-bundle-green hover:text-white"
            >
              <Flag className="w-4 h-4 mr-2" />
              Manage All Rounds
            </Button>
            <Button 
              variant="outline" 
              onClick={() => navigate('/admin/bundle-wins')}
              className="border-bundle-gold text-bundle-green hover:bg-bundle-gold hover:text-bundle-green"
            >
              <DollarSign className="w-4 h-4 mr-2" />
              Manage Bundle Wins
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/video-maker')}
              data-testid="admin-video-maker-btn"
              className="border-purple-500 text-purple-700 hover:bg-purple-500 hover:text-white"
            >
              <Play className="w-4 h-4 mr-2" />
              AI Video Maker
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/news')}
              data-testid="admin-news-btn"
              className="border-emerald-500 text-emerald-700 hover:bg-emerald-500 hover:text-white"
            >
              <Flag className="w-4 h-4 mr-2" />
              Post DMV News
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/announcements')}
              data-testid="admin-announcements-btn"
              className="border-blue-500 text-blue-700 hover:bg-blue-500 hover:text-white"
            >
              <Mail className="w-4 h-4 mr-2" />
              Send Announcement
            </Button>
            <Button
              variant="outline"
              onClick={sendWeekendDigest}
              disabled={sendingDigest}
              data-testid="admin-weekend-digest-btn"
              className="border-amber-500 text-amber-700 hover:bg-amber-500 hover:text-white"
            >
              <Mail className="w-4 h-4 mr-2" />
              {sendingDigest ? 'Sending...' : 'Weekend Digest'}
            </Button>
            <Button
              variant="outline"
              onClick={backfillBirdieStats}
              disabled={backfillingBirdies}
              data-testid="admin-backfill-birdies-btn"
              className="border-rose-500 text-rose-700 hover:bg-rose-500 hover:text-white"
              title="Rescan all posted rounds and recompute birdie/eagle stats for entries missing them"
            >
              <Flag className="w-4 h-4 mr-2" />
              {backfillingBirdies ? 'Recomputing...' : 'Recompute Birdie Stats'}
            </Button>
          </div>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            {loadingData ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-bundle-green mx-auto"></div>
              </div>
            ) : analytics && (
              <div className="space-y-6">
                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="bg-white border-bundle-cream-dark">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-3 mb-2">
                        <Users className="w-5 h-5 text-bundle-green" />
                        <span className="font-body text-xs uppercase tracking-wide text-gray-500">Total Members</span>
                      </div>
                      <p className="font-heading text-3xl font-bold text-bundle-green">{analytics.total_members}</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-white border-bundle-cream-dark">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-3 mb-2">
                        <UserCheck className="w-5 h-5 text-bundle-gold" />
                        <span className="font-body text-xs uppercase tracking-wide text-gray-500">Active Members</span>
                      </div>
                      <p className="font-heading text-3xl font-bold text-bundle-green">{analytics.active_members}</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-white border-bundle-cream-dark">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-3 mb-2">
                        <DollarSign className="w-5 h-5 text-green-600" />
                        <span className="font-body text-xs uppercase tracking-wide text-gray-500">Total Revenue</span>
                      </div>
                      <p className="font-heading text-3xl font-bold text-bundle-green">${analytics.total_revenue}</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-white border-bundle-cream-dark">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-3 mb-2">
                        <TrendingUp className="w-5 h-5 text-blue-600" />
                        <span className="font-body text-xs uppercase tracking-wide text-gray-500">Conversion</span>
                      </div>
                      <p className="font-heading text-3xl font-bold text-bundle-green">{analytics.conversion_rate}%</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Activity Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="bg-bundle-cream/50 border-bundle-cream-dark">
                    <CardContent className="p-5 text-center">
                      <p className="font-heading text-2xl font-bold text-bundle-green">{analytics.total_stories}</p>
                      <p className="font-body text-sm text-gray-600">Bundle Stories</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-bundle-cream/50 border-bundle-cream-dark">
                    <CardContent className="p-5 text-center">
                      <p className="font-heading text-2xl font-bold text-bundle-green">{analytics.total_rounds}</p>
                      <p className="font-body text-sm text-gray-600">Rounds Posted</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-bundle-cream/50 border-bundle-cream-dark">
                    <CardContent className="p-5 text-center">
                      <p className="font-heading text-2xl font-bold text-bundle-green">{analytics.total_events}</p>
                      <p className="font-body text-sm text-gray-600">Events Created</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-bundle-cream/50 border-bundle-cream-dark">
                    <CardContent className="p-5 text-center">
                      <p className="font-heading text-2xl font-bold text-bundle-green">{analytics.recent_signups}</p>
                      <p className="font-body text-sm text-gray-600">New (30 days)</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Signup Activity Section */}
                {signupActivity && (
                  <Card className="bg-white border-bundle-cream-dark">
                    <CardHeader className="border-b border-bundle-cream-dark bg-gradient-to-r from-bundle-green/5 to-bundle-gold/5">
                      <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <UserPlus className="w-5 h-5 text-bundle-green" />
                        Signup Activity
                        {signupActivity.signups_today > 0 && (
                          <Badge className="bg-bundle-gold text-bundle-green-dark ml-2">
                            {signupActivity.signups_today} today
                          </Badge>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      {/* Signup Stats Row */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-bundle-cream/50 rounded-xl p-4 text-center">
                          <p className="font-heading text-3xl font-bold text-bundle-green">{signupActivity.signups_today}</p>
                          <p className="font-body text-xs text-gray-500 uppercase tracking-wide">Today</p>
                        </div>
                        <div className="bg-bundle-cream/50 rounded-xl p-4 text-center">
                          <p className="font-heading text-3xl font-bold text-bundle-green">{signupActivity.signups_7d}</p>
                          <p className="font-body text-xs text-gray-500 uppercase tracking-wide">Last 7 Days</p>
                        </div>
                        <div className="bg-bundle-cream/50 rounded-xl p-4 text-center">
                          <p className="font-heading text-3xl font-bold text-bundle-green">{signupActivity.signups_30d}</p>
                          <p className="font-body text-xs text-gray-500 uppercase tracking-wide">Last 30 Days</p>
                        </div>
                        <div className={`rounded-xl p-4 text-center ${signupActivity.growth_pct >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                          <div className="flex items-center justify-center gap-1">
                            {signupActivity.growth_pct >= 0 ? (
                              <TrendingUp className="w-5 h-5 text-green-600" />
                            ) : (
                              <TrendingDown className="w-5 h-5 text-red-600" />
                            )}
                            <p className={`font-heading text-3xl font-bold ${signupActivity.growth_pct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {signupActivity.growth_pct > 0 ? '+' : ''}{signupActivity.growth_pct}%
                            </p>
                          </div>
                          <p className="font-body text-xs text-gray-500 uppercase tracking-wide">30d Growth</p>
                        </div>
                      </div>

                      {/* Mini Bar Chart */}
                      <div className="mb-6">
                        <p className="font-body text-xs text-gray-500 uppercase tracking-wide mb-3">Last 7 Days Activity</p>
                        <div className="flex items-end gap-2 h-24">
                          {signupActivity.daily_signups.map((day, idx) => {
                            const maxCount = Math.max(...signupActivity.daily_signups.map(d => d.count), 1);
                            const heightPct = (day.count / maxCount) * 100;
                            return (
                              <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                                <span className="font-body text-xs text-gray-600">{day.count}</span>
                                <div 
                                  className={`w-full rounded-t transition-all ${day.count > 0 ? 'bg-bundle-green' : 'bg-gray-200'}`}
                                  style={{ height: `${Math.max(heightPct, 8)}%`, minHeight: '4px' }}
                                />
                                <span className="font-body text-xs text-gray-500">{day.day}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Recent Members */}
                      <div>
                        <p className="font-body text-xs text-gray-500 uppercase tracking-wide mb-3">Recent Registrations</p>
                        <div className="space-y-2">
                          {signupActivity.recent_members.slice(0, 5).map((member) => (
                            <div key={member.user_id} className="flex items-center gap-3 p-3 bg-bundle-cream/30 rounded-lg hover:bg-bundle-cream/50 transition-colors">
                              {member.picture ? (
                                <img src={member.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                              ) : (
                                <div className="w-10 h-10 rounded-full bg-bundle-green/20 flex items-center justify-center">
                                  <span className="font-heading text-sm text-bundle-green">
                                    {member.name?.charAt(0) || '?'}
                                  </span>
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="font-heading font-semibold text-bundle-green truncate">{member.name}</p>
                                <p className="font-body text-xs text-gray-500 truncate">{member.email}</p>
                              </div>
                              <div className="text-right">
                                <Badge variant="outline" className={member.is_member ? 'border-green-500 text-green-600' : 'border-gray-300 text-gray-500'}>
                                  {member.membership_type || 'member'}
                                </Badge>
                                <p className="font-body text-xs text-gray-400 mt-1">
                                  {member.created_at ? new Date(member.created_at).toLocaleDateString() : 'N/A'}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                        {signupActivity.recent_members.length > 5 && (
                          <Button 
                            variant="ghost" 
                            className="w-full mt-3 text-bundle-green hover:bg-bundle-cream"
                            onClick={() => setActiveTab('members')}
                          >
                            View All {analytics.total_members} Members
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>

          {/* Members Tab */}
          <TabsContent value="members">
            <Card className="bg-white border-bundle-cream-dark overflow-hidden">
              <CardHeader className="border-b border-bundle-cream-dark bg-bundle-cream/30">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-bundle-green" />
                  Member Management ({members.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {loadingData ? (
                  <div className="p-12 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-bundle-green mx-auto"></div>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-bundle-cream/30">
                          <TableHead className="font-body text-xs uppercase tracking-wide text-gray-500">Member</TableHead>
                          <TableHead className="font-body text-xs uppercase tracking-wide text-gray-500">Email</TableHead>
                          <TableHead className="font-body text-xs uppercase tracking-wide text-gray-500 text-center">Status</TableHead>
                          <TableHead className="font-body text-xs uppercase tracking-wide text-gray-500 text-center">Role</TableHead>
                          <TableHead className="font-body text-xs uppercase tracking-wide text-gray-500 text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {members.map((member) => (
                          <TableRow key={member.user_id} className="hover:bg-bundle-cream/30">
                            <TableCell>
                              <div className="flex items-center gap-3">
                                {member.picture ? (
                                  <img src={member.picture} alt="" className="w-8 h-8 rounded-full" />
                                ) : (
                                  <div className="w-8 h-8 bg-bundle-green rounded-full flex items-center justify-center text-white text-sm">
                                    {member.name?.charAt(0)}
                                  </div>
                                )}
                                <span className="font-body font-medium text-bundle-green">{member.name}</span>
                              </div>
                            </TableCell>
                            <TableCell className="font-body text-gray-600 text-sm">{member.email}</TableCell>
                            <TableCell className="text-center">
                              <Badge 
                                variant={member.is_member ? "default" : "secondary"}
                                className={member.is_member ? "bg-bundle-green" : "bg-gray-200 text-gray-600"}
                              >
                                {member.is_member ? 'Active' : 'Free'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              {member.is_admin && (
                                <Badge className="bg-bundle-gold text-bundle-green-dark">
                                  <Crown className="w-3 h-3 mr-1" />
                                  Director
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setEditingMember(member)}
                                  className="text-bundle-green hover:text-bundle-green-dark"
                                  title="Edit profile"
                                  data-testid={`edit-member-${member.user_id}`}
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => toggleMembership(member.user_id, member.is_member)}
                                  className={member.is_member ? "text-red-500 hover:text-red-700" : "text-bundle-green hover:text-bundle-green-dark"}
                                >
                                  {member.is_member ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => toggleAdmin(member.user_id, member.is_admin)}
                                  className={member.is_admin ? "text-bundle-gold" : "text-gray-400"}
                                  title={member.is_admin ? "Remove admin" : "Make admin"}
                                >
                                  <Crown className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setMemberRole(member.user_id, member.role === 'fundraiser' ? 'member' : 'fundraiser')}
                                  className={member.role === 'fundraiser' ? 'text-bundle-gold' : 'text-gray-400'}
                                  title={member.role === 'fundraiser' ? 'Demote from fundraiser' : 'Make fundraiser'}
                                  data-testid={`toggle-fundraiser-${member.user_id}`}
                                >
                                  <Trophy className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => resendWelcomeEmail(member)}
                                  disabled={!!resendingWelcome[member.user_id]}
                                  className="text-blue-500 hover:text-blue-700"
                                  title="Resend welcome email with a new temporary password"
                                  data-testid={`resend-welcome-${member.user_id}`}
                                >
                                  <Send className={`w-4 h-4 ${resendingWelcome[member.user_id] ? 'animate-pulse' : ''}`} />
                                </Button>
                                {member.user_id !== user.user_id && (
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700">
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle className="font-heading">Delete Member?</AlertDialogTitle>
                                        <AlertDialogDescription className="font-body">
                                          This will permanently delete {member.name} and all their data.
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                        <AlertDialogAction
                                          onClick={() => deleteMember(member.user_id)}
                                          className="bg-red-500 hover:bg-red-700"
                                        >
                                          Delete
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Stories Tab */}
          <TabsContent value="stories">
            <Card className="bg-white border-bundle-cream-dark overflow-hidden">
              <CardHeader className="border-b border-bundle-cream-dark bg-bundle-cream/30">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Video className="w-5 h-5 text-bundle-green" />
                  Story Moderation ({stories.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {loadingData ? (
                  <div className="p-12 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-bundle-green mx-auto"></div>
                  </div>
                ) : stories.length === 0 ? (
                  <div className="p-12 text-center">
                    <Video className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="font-body text-gray-500">No stories to moderate</p>
                  </div>
                ) : (
                  <div className="divide-y divide-bundle-cream-dark">
                    {stories.map((story) => (
                      <div key={story.story_id} className="p-4 hover:bg-bundle-cream/30 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-4 flex-1">
                          {story.media_url && (
                            <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                              {story.media_type === 'video' ? (
                                <video src={story.media_url} className="w-full h-full object-cover" />
                              ) : (
                                <img src={story.media_url} alt="" className="w-full h-full object-cover" />
                              )}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-body font-semibold text-bundle-green">
                              {story.winner_name} won ${story.amount_won}
                            </p>
                            <p className="font-body text-sm text-gray-500 truncate">{story.description}</p>
                            <p className="font-body text-xs text-gray-400">
                              By {story.user_name} • {new Date(story.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 flex-shrink-0">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle className="font-heading">Delete Story?</AlertDialogTitle>
                              <AlertDialogDescription className="font-body">
                                This will permanently delete this story.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deleteStory(story.story_id)}
                                className="bg-red-500 hover:bg-red-700"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events">
            {/* Pending Events Section */}
            {pendingEvents.length > 0 && (
              <Card className="bg-white border-bundle-gold mb-6 overflow-hidden">
                <CardHeader className="border-b border-bundle-gold bg-bundle-gold/10">
                  <CardTitle className="font-heading text-lg flex items-center gap-2 text-bundle-gold-dark">
                    <Clock className="w-5 h-5" />
                    Pending Approval ({pendingEvents.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-bundle-cream-dark">
                    {pendingEvents.map((event) => (
                      <div key={event.event_id} className="p-4 bg-bundle-gold/5 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-4 flex-1">
                          <div className="w-12 h-12 bg-bundle-gold/20 rounded-lg flex flex-col items-center justify-center flex-shrink-0">
                            <span className="font-body text-xs text-bundle-gold-dark uppercase">
                              {new Date(event.date).toLocaleDateString('en-US', { month: 'short' })}
                            </span>
                            <span className="font-heading text-lg font-bold text-bundle-green">
                              {new Date(event.date).getDate()}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-body font-semibold text-bundle-green">{event.title}</p>
                            <p className="font-body text-sm text-gray-600">{event.location} • {event.time}</p>
                            <p className="font-body text-xs text-gray-500">
                              Submitted by <strong>{event.created_by_name || 'Unknown'}</strong>
                              {event.created_by_email && ` (${event.created_by_email})`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button 
                            onClick={() => approveEvent(event.event_id)}
                            size="sm"
                            className="bg-bundle-green hover:bg-bundle-green-dark text-white"
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button 
                            onClick={() => rejectEvent(event.event_id)}
                            variant="outline"
                            size="sm"
                            className="border-red-300 text-red-600 hover:bg-red-50"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* All Events */}
            <Card className="bg-white border-bundle-cream-dark overflow-hidden">
              <CardHeader className="border-b border-bundle-cream-dark bg-bundle-cream/30 flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-bundle-green" />
                  Approved Events ({events.filter(e => e.status === 'approved' || !e.status).length})
                </CardTitle>
                <Button 
                  onClick={() => setShowCreateEvent(true)}
                  className="bg-bundle-green hover:bg-bundle-green-dark text-white"
                >
                  <PlusCircle className="w-4 h-4 mr-2" />
                  Create Event
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                {loadingData ? (
                  <div className="p-12 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-bundle-green mx-auto"></div>
                  </div>
                ) : events.length === 0 ? (
                  <div className="p-12 text-center">
                    <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="font-body text-gray-500">No events created yet</p>
                    <Button 
                      onClick={() => setShowCreateEvent(true)}
                      className="mt-4 bg-bundle-green hover:bg-bundle-green-dark text-white"
                    >
                      <PlusCircle className="w-4 h-4 mr-2" />
                      Create Your First Event
                    </Button>
                  </div>
                ) : (
                  <div className="divide-y divide-bundle-cream-dark">
                    {events.map((event) => (
                      <div key={event.event_id} className="p-4 hover:bg-bundle-cream/30 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-4 flex-1">
                          <div className="w-12 h-12 bg-bundle-cream rounded-lg flex flex-col items-center justify-center flex-shrink-0">
                            <span className="font-body text-xs text-bundle-gold-dark uppercase">
                              {new Date(event.date).toLocaleDateString('en-US', { month: 'short' })}
                            </span>
                            <span className="font-heading text-lg font-bold text-bundle-green">
                              {new Date(event.date).getDate()}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-body font-semibold text-bundle-green">{event.title}</p>
                            <p className="font-body text-sm text-gray-500">{event.location} • {event.time}</p>
                            <p className="font-body text-xs text-gray-400">
                              {event.participants?.length || 0} participants
                              {event.created_by_name && ` • by ${event.created_by_name}`}
                            </p>
                          </div>
                        </div>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 flex-shrink-0">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle className="font-heading">Delete Event?</AlertDialogTitle>
                              <AlertDialogDescription className="font-body">
                                This will permanently delete "{event.title}" and remove all signups.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deleteEvent(event.event_id)}
                                className="bg-red-500 hover:bg-red-700"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Promo Videos Tab */}
          <TabsContent value="promos">
            <Card className="bg-white border-bundle-cream-dark overflow-hidden">
              <CardHeader className="border-b border-bundle-cream-dark bg-bundle-cream/30 flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Play className="w-5 h-5 text-bundle-green" />
                  Promotional Videos ({promoVideos.length})
                </CardTitle>
                <Button 
                  onClick={() => {
                    setEditingPromo(null);
                    setNewPromo({ title: '', description: '', video_url: '', embed_url: '', external_link: '', is_active: true });
                    setShowPromoModal(true);
                  }}
                  className="bg-bundle-green hover:bg-bundle-green-dark text-white"
                >
                  <PlusCircle className="w-4 h-4 mr-2" />
                  Add Video
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                {loadingData ? (
                  <div className="p-12 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-bundle-green mx-auto"></div>
                  </div>
                ) : promoVideos.length === 0 ? (
                  <div className="p-12 text-center">
                    <Play className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="font-body text-gray-500">No promotional videos yet</p>
                    <Button 
                      onClick={() => {
                        setEditingPromo(null);
                        setNewPromo({ title: '', description: '', video_url: '', embed_url: '', external_link: '', is_active: true });
                        setShowPromoModal(true);
                      }}
                      className="mt-4 bg-bundle-green hover:bg-bundle-green-dark text-white"
                    >
                      <PlusCircle className="w-4 h-4 mr-2" />
                      Add Your First Video
                    </Button>
                  </div>
                ) : (
                  <div className="divide-y divide-bundle-cream-dark">
                    {promoVideos.map((video) => (
                      <div key={video.video_id} className="p-4 hover:bg-bundle-cream/30 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-4 flex-1">
                          <div className="w-24 h-14 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                            {video.embed_iframe ? (
                              <iframe
                                src={video.embed_iframe}
                                className="w-full h-full"
                                frameBorder="0"
                                title={video.title}
                              />
                            ) : (
                              <Play className="w-8 h-8 text-gray-400" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-body font-semibold text-bundle-green">{video.title}</p>
                              {video.is_active ? (
                                <Badge className="bg-green-100 text-green-700 text-xs">Active</Badge>
                              ) : (
                                <Badge className="bg-gray-100 text-gray-500 text-xs">Hidden</Badge>
                              )}
                            </div>
                            <p className="font-body text-sm text-gray-500 truncate">{video.description || 'No description'}</p>
                            <p className="font-body text-xs text-gray-400">
                              {video.views || 0} views • {video.embed_type ? `${video.embed_type} embed` : 'Uploaded video'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => togglePromoVideo(video.video_id)}
                            className="text-gray-500 hover:text-bundle-green"
                          >
                            {video.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => openEditPromo(video)}
                            className="text-gray-500 hover:text-bundle-green"
                          >
                            Edit
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle className="font-heading">Delete Video?</AlertDialogTitle>
                                <AlertDialogDescription className="font-body">
                                  This will permanently delete "{video.title}".
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => deletePromoVideo(video.video_id)}
                                  className="bg-red-500 hover:bg-red-700"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invitation Requests Tab */}
          <TabsContent value="invites">
            <InvitationRequestsPanel />
          </TabsContent>

          <TabsContent value="tags">
            <BundleTagsAdminPanel />
          </TabsContent>

          <TabsContent value="quick-create">
            <QuickCreateUserPanel />
          </TabsContent>
        </Tabs>

        {/* Create Event Dialog */}
        <Dialog open={showCreateEvent} onOpenChange={setShowCreateEvent}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-bundle-green flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Create New Event
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={createEvent} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title" className="font-body text-bundle-green-dark">Event Title *</Label>
                <Input
                  id="title"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                  placeholder="e.g., Weekly Bundle Game"
                  required
                  className="border-bundle-cream-dark focus:border-bundle-green"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date" className="font-body text-bundle-green-dark">Date *</Label>
                  <Input
                    id="date"
                    type="date"
                    value={newEvent.date}
                    onChange={(e) => setNewEvent({...newEvent, date: e.target.value})}
                    required
                    className="border-bundle-cream-dark focus:border-bundle-green"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="time" className="font-body text-bundle-green-dark">Time *</Label>
                  <Input
                    id="time"
                    type="time"
                    value={newEvent.time}
                    onChange={(e) => setNewEvent({...newEvent, time: e.target.value})}
                    required
                    className="border-bundle-cream-dark focus:border-bundle-green"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="location" className="font-body text-bundle-green-dark">Location *</Label>
                <Input
                  id="location"
                  value={newEvent.location}
                  onChange={(e) => setNewEvent({...newEvent, location: e.target.value})}
                  placeholder="e.g., Pine Valley Golf Club"
                  required
                  className="border-bundle-cream-dark focus:border-bundle-green"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="event_type" className="font-body text-bundle-green-dark">Event Type</Label>
                <select
                  id="event_type"
                  value={newEvent.event_type}
                  onChange={(e) => setNewEvent({...newEvent, event_type: e.target.value})}
                  className="w-full h-10 px-3 rounded-md border border-bundle-cream-dark focus:border-bundle-green focus:outline-none font-body"
                >
                  <option value="weekly_game">Weekly Game</option>
                  <option value="tournament">Tournament</option>
                  <option value="social">Social Event</option>
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="max_participants" className="font-body text-bundle-green-dark">Max Participants</Label>
                  <Input
                    id="max_participants"
                    type="number"
                    value={newEvent.max_participants}
                    onChange={(e) => setNewEvent({...newEvent, max_participants: e.target.value})}
                    placeholder="e.g., 24"
                    className="border-bundle-cream-dark focus:border-bundle-green"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="entry_fee" className="font-body text-bundle-green-dark">Entry Fee ($)</Label>
                  <Input
                    id="entry_fee"
                    type="number"
                    step="0.01"
                    value={newEvent.entry_fee}
                    onChange={(e) => setNewEvent({...newEvent, entry_fee: e.target.value})}
                    placeholder="e.g., 50"
                    className="border-bundle-cream-dark focus:border-bundle-green"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description" className="font-body text-bundle-green-dark">Description</Label>
                <textarea
                  id="description"
                  value={newEvent.description}
                  onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                  placeholder="Event details, rules, etc."
                  rows={3}
                  className="w-full px-3 py-2 rounded-md border border-bundle-cream-dark focus:border-bundle-green focus:outline-none font-body resize-none"
                />
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCreateEvent(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-bundle-green hover:bg-bundle-green-dark text-white">
                  Create Event
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Promo Video Dialog */}
        <Dialog open={showPromoModal} onOpenChange={(open) => {
          setShowPromoModal(open);
          if (!open) setEditingPromo(null);
        }}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-bundle-green flex items-center gap-2">
                <Play className="w-5 h-5" />
                {editingPromo ? 'Edit Promotional Video' : 'Add Promotional Video'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="font-body text-bundle-green-dark">Title *</Label>
                <Input
                  value={newPromo.title}
                  onChange={(e) => setNewPromo({...newPromo, title: e.target.value})}
                  placeholder="e.g., Spring Golf Special"
                  className="border-bundle-cream-dark focus:border-bundle-green"
                />
              </div>

              <div className="space-y-2">
                <Label className="font-body text-bundle-green-dark">Description</Label>
                <Textarea
                  value={newPromo.description}
                  onChange={(e) => setNewPromo({...newPromo, description: e.target.value})}
                  placeholder="What's this video about?"
                  rows={2}
                  className="border-bundle-cream-dark focus:border-bundle-green"
                />
              </div>

              <div className="space-y-3">
                <Label className="font-body text-bundle-green-dark">Video Source</Label>
                
                {/* Upload Option */}
                <div className="border border-bundle-cream-dark rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <Upload className="w-5 h-5 text-bundle-green" />
                    <span className="font-body font-medium text-bundle-green">Upload Video</span>
                  </div>
                  <input
                    ref={videoFileRef}
                    type="file"
                    accept="video/mp4,video/quicktime,video/webm"
                    onChange={handleVideoUpload}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => videoFileRef.current?.click()}
                    disabled={uploadingVideo}
                    className="w-full"
                  >
                    {uploadingVideo ? 'Uploading...' : newPromo.video_url ? 'Change Video' : 'Select Video File'}
                  </Button>
                  {newPromo.video_url && (
                    <p className="text-sm text-green-600 mt-2">✓ Video uploaded</p>
                  )}
                </div>

                {/* OR Divider */}
                <div className="flex items-center gap-4">
                  <div className="flex-1 h-px bg-gray-200"></div>
                  <span className="font-body text-sm text-gray-400">OR</span>
                  <div className="flex-1 h-px bg-gray-200"></div>
                </div>

                {/* Embed Option */}
                <div className="border border-bundle-cream-dark rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <Link className="w-5 h-5 text-bundle-green" />
                    <span className="font-body font-medium text-bundle-green">YouTube / Vimeo URL</span>
                  </div>
                  <Input
                    value={newPromo.embed_url}
                    onChange={(e) => setNewPromo({...newPromo, embed_url: e.target.value, video_url: ''})}
                    placeholder="https://youtube.com/watch?v=..."
                    className="border-bundle-cream-dark focus:border-bundle-green"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="font-body text-bundle-green-dark">External Link (Optional)</Label>
                <Input
                  value={newPromo.external_link}
                  onChange={(e) => setNewPromo({...newPromo, external_link: e.target.value})}
                  placeholder="https://yoursite.com/promo"
                  className="border-bundle-cream-dark focus:border-bundle-green"
                />
                <p className="text-xs text-gray-500">Link shown below the video for users to learn more</p>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={newPromo.is_active}
                  onChange={(e) => setNewPromo({...newPromo, is_active: e.target.checked})}
                  className="rounded border-gray-300 text-bundle-green focus:ring-bundle-green"
                />
                <Label htmlFor="is_active" className="font-body text-sm text-gray-600">
                  Show on member dashboard
                </Label>
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowPromoModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={savePromoVideo}
                  className="bg-bundle-green hover:bg-bundle-green-dark text-white"
                >
                  {editingPromo ? 'Save Changes' : 'Add Video'}
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>

        {/* Admin — edit any member's profile. Writes to admin_audit_log server-side. */}
        <EditMemberModal
          member={editingMember}
          open={!!editingMember}
          onClose={() => setEditingMember(null)}
          onSaved={(fresh) => {
            setMembers((prev) => prev.map((m) => m.user_id === fresh.user_id ? { ...m, ...fresh } : m));
          }}
        />
      </div>
    </div>
  );
}

function InvitationRequestsPanel() {
  const [requests, setRequests] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, declined: 0 });
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const fetchAll = useCallback(async (status = filter) => {
    setLoading(true);
    try {
      const url = status === 'all' ? `${API}/admin/invitation-requests` : `${API}/admin/invitation-requests?status=${status}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error('Fetch failed');
      const data = await res.json();
      setRequests(data.requests || []);
      setCounts(data.counts || { pending: 0, approved: 0, declined: 0 });
    } catch (e) {
      console.error('invitation list failed', e);
      toast.error('Could not load invitation requests');
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchAll(filter); }, [filter, fetchAll]);

  const approve = async (id) => {
    setBusy(id);
    try {
      const res = await fetch(`${API}/admin/invitation-requests/${id}/approve`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error('Approve failed');
      toast.success('Approved — invite code emailed');
      fetchAll(filter);
    } catch (e) { toast.error('Approve failed'); }
    finally { setBusy(null); }
  };

  const decline = async (id) => {
    if (!window.confirm('Decline this request?')) return;
    setBusy(id);
    try {
      const res = await fetch(`${API}/admin/invitation-requests/${id}/decline`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) throw new Error('Decline failed');
      toast.success('Declined');
      fetchAll(filter);
    } catch (e) { toast.error('Decline failed'); }
    finally { setBusy(null); }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="font-heading text-bundle-green flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Invitation Requests
          </CardTitle>
          <div className="flex gap-2 flex-wrap">
            {['pending','approved','declined','all'].map(s => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                data-testid={`invite-filter-${s}`}
                className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${
                  filter === s ? 'bg-bundle-green text-white' : 'bg-bundle-cream text-gray-700 hover:bg-bundle-cream-dark'
                }`}
              >
                {s} {s !== 'all' && counts[s] !== undefined && <span className="ml-1 opacity-70">({counts[s]})</span>}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-gray-400 py-6 text-center">Loading…</div>
        ) : requests.length === 0 ? (
          <div className="text-sm text-gray-400 py-10 text-center">No {filter !== 'all' ? filter : ''} requests.</div>
        ) : (
          <div className="space-y-3">
            {requests.map(r => (
              <div key={r.request_id} data-testid={`invite-request-${r.request_id}`} className="border border-bundle-cream-dark rounded-lg p-3 bg-white">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-bundle-green">{r.first_name} {r.last_name}</p>
                    <a href={`mailto:${r.email}`} className="text-xs text-bundle-green hover:underline truncate block">{r.email}</a>
                    <p className="text-[10px] text-gray-400 mt-0.5">{new Date(r.created_at).toLocaleString()}</p>
                  </div>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                    r.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                    r.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                    'bg-gray-100 text-gray-500'
                  }`}>{r.status}</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] text-gray-600">
                  <div><span className="text-gray-400">Phone:</span> {r.phone || '—'}</div>
                  <div><span className="text-gray-400">Location:</span> {[r.city, r.state].filter(Boolean).join(', ') || '—'}</div>
                  <div><span className="text-gray-400">Handicap:</span> {r.handicap || '—'}</div>
                  <div><span className="text-gray-400">Home Course:</span> {r.home_course || '—'}</div>
                </div>
                {(r.referred_by || r.how_heard) && (
                  <div className="mt-2 text-[11px] text-gray-600">
                    {r.referred_by && <div><span className="text-gray-400">Referred by:</span> {r.referred_by}</div>}
                    {r.how_heard && <div><span className="text-gray-400">How heard:</span> {r.how_heard}</div>}
                  </div>
                )}
                {r.status === 'pending' && (
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" onClick={() => approve(r.request_id)} disabled={busy === r.request_id} data-testid={`invite-approve-${r.request_id}`} className="bg-bundle-green hover:bg-bundle-green-dark text-white">
                      <Check className="w-3.5 h-3.5 mr-1" /> Approve & Send Code
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => decline(r.request_id)} disabled={busy === r.request_id} data-testid={`invite-decline-${r.request_id}`}>
                      <X className="w-3.5 h-3.5 mr-1" /> Decline
                    </Button>
                  </div>
                )}
                {r.status === 'approved' && r.invite_code_sent && (
                  <div className="mt-2 text-[11px] text-emerald-700">Sent code: <code className="font-mono font-bold">{r.invite_code_sent}</code></div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ──────────────────────────────────────────────────────────────────────
// Bundle Tags Admin Panel — review/approve/reject member-suggested tags
// ──────────────────────────────────────────────────────────────────────
function BundleTagsAdminPanel() {
  const [pending, setPending] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyName, setBusyName] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pendRes, catRes] = await Promise.all([
        fetch(`${API}/admin/tags/pending`, { credentials: 'include' }),
        fetch(`${API}/tags/catalog`),
      ]);
      const pendData = await pendRes.json();
      const catData = await catRes.json();
      setPending(pendData.suggestions || []);
      setCatalog(catData.catalog || {});
    } catch {
      toast.error('Failed to load Bundle Tags data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const review = async (name, action) => {
    setBusyName(name);
    try {
      const res = await fetch(`${API}/admin/tags/${name}/${action}`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || `Failed to ${action}`);
        return;
      }
      toast.success(`#${name} ${action === 'approve' ? 'approved' : 'rejected'}`);
      fetchAll();
    } finally {
      setBusyName(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Pending suggestions */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-bundle-green flex items-center gap-2">
            <Tag className="w-5 h-5" /> Pending Tag Suggestions
            {pending.length > 0 && (
              <Badge className="bg-bundle-gold text-bundle-green-dark ml-2">{pending.length}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : pending.length === 0 ? (
            <p className="text-sm text-gray-500">No pending suggestions. All caught up.</p>
          ) : (
            <div className="space-y-3">
              {pending.map((p) => (
                <div key={p.name} className="flex items-start justify-between gap-3 rounded-xl border border-bundle-cream-dark bg-bundle-cream/30 p-3" data-testid={`tag-pending-${p.name}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center rounded-full bg-bundle-green text-white px-2.5 py-1 text-xs font-bold">#{p.name}</span>
                      <span className="text-[10px] uppercase tracking-widest font-bold text-gray-500">{p.category}</span>
                    </div>
                    {p.reason && <p className="text-xs text-gray-700 mt-1.5">{p.reason}</p>}
                    <p className="text-[10px] text-gray-400 mt-1">Suggested by {p.suggested_by_name || 'unknown'} · {p.created_at?.slice(0,10)}</p>
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0">
                    <Button
                      size="sm"
                      onClick={() => review(p.name, 'approve')}
                      disabled={busyName === p.name}
                      data-testid={`tag-approve-${p.name}`}
                      className="bg-bundle-green hover:bg-bundle-green-dark text-white"
                    >
                      <Check className="w-3.5 h-3.5 mr-1" /> Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => review(p.name, 'reject')}
                      disabled={busyName === p.name}
                      data-testid={`tag-reject-${p.name}`}
                      className="border-red-300 text-red-600 hover:bg-red-50"
                    >
                      <X className="w-3.5 h-3.5 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Live catalog (read-only reference) */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-bundle-green">Live Tag Catalog</CardTitle>
        </CardHeader>
        <CardContent>
          {!catalog ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : (
            <div className="space-y-4">
              {Object.entries(catalog).map(([cat, tags]) => (
                <div key={cat}>
                  <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-1.5">
                    {cat} <span className="text-gray-400 normal-case">· {tags.length}</span>
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {tags.map((t) => (
                      <span key={t} className="inline-flex items-center rounded-full bg-bundle-green/10 text-bundle-green border border-bundle-green/20 px-2 py-0.5 text-[11px] font-bold">
                        #{t}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────────
// Quick-Create User Panel — provision Fundraiser or Director accounts with one form.
// Bypasses the member application: account is auto-active with lifetime membership,
// a temp password is generated, and a welcome email is sent.
// ──────────────────────────────────────────────────────────────────────
function QuickCreateUserPanel() {
  const [form, setForm] = useState({
    email: '', name: '', role: 'fundraiser', phone: '', city: '', state: ''
  });
  const [creating, setCreating] = useState(false);
  const [lastCreated, setLastCreated] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.name.trim()) {
      toast.error('Email and name are required');
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(`${API}/admin/users/quick-create`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.email.trim(),
          name: form.name.trim(),
          role: form.role,
          phone: form.phone || null,
          city: form.city || null,
          state: form.state || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Failed to create user');
        return;
      }
      toast.success(`${form.role === 'director' ? 'Director' : 'Fundraiser'} created. Welcome email sent to ${form.email}.`);
      setLastCreated({ ...data, name: form.name });
      setForm({ email: '', name: '', role: form.role, phone: '', city: '', state: '' });
    } catch {
      toast.error('Network error');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-heading text-bundle-green flex items-center gap-2">
          <UserPlus className="w-5 h-5" /> Add Fundraiser or Director
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-gray-600 mb-4">
          Creates a lifetime account with a temporary password and emails the credentials. They skip the member application form entirely &mdash; they land on the Dashboard and can complete their profile later.
        </p>

        <form onSubmit={submit} className="space-y-3" data-testid="quick-create-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold text-gray-700">Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@email.com" required data-testid="qc-email" />
            </div>
            <div>
              <Label className="text-xs font-bold text-gray-700">Full Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" required data-testid="qc-name" />
            </div>
            <div>
              <Label className="text-xs font-bold text-gray-700">Role *</Label>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white"
                data-testid="qc-role"
              >
                <option value="fundraiser">Fundraiser</option>
                <option value="director">Golf Club Director</option>
              </select>
            </div>
            <div>
              <Label className="text-xs font-bold text-gray-700">Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Optional" data-testid="qc-phone" />
            </div>
            <div>
              <Label className="text-xs font-bold text-gray-700">City</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Optional" data-testid="qc-city" />
            </div>
            <div>
              <Label className="text-xs font-bold text-gray-700">State</Label>
              <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="Optional" data-testid="qc-state" />
            </div>
          </div>
          <Button type="submit" disabled={creating} className="bg-bundle-green hover:bg-bundle-green-dark text-white font-bold w-full mt-2" data-testid="qc-submit">
            {creating ? 'Creating…' : 'Create & Send Welcome Email'}
          </Button>
        </form>

        {lastCreated && (
          <div className="mt-5 rounded-xl bg-bundle-green/5 border border-bundle-green/20 p-3" data-testid="qc-last-created">
            <p className="text-xs font-bold text-bundle-green mb-1">Last created</p>
            <p className="text-sm text-bundle-green">{lastCreated.name} &middot; <span className="text-gray-600">{lastCreated.email}</span></p>
            <p className="text-[11px] text-gray-500 mt-1">Welcome email sent. They&rsquo;ll sign in with the temp password from the email and land directly on the Dashboard.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

